// features/inventory/pages/AdjustStockPage.tsx
/**
 * Adjust Stock Page — Manual stock adjustments with barcode scanner support.
 *
 * Route: /inventory/adjust
 *
 * Features:
 * - Adjustment type selection (correction, waste, damage, found in count)
 * - Branch selection for multi-store setups
 * - Multi-line product entry with search and barcode scanning
 * - Inline product search with dropdown
 * - Auto-populated UOM and unit cost from product data
 * - Current stock display for reference
 * - Batch number and expiry date fields for tracked products
 * - Item notes for each adjustment line
 * - Global notes for the entire adjustment
 * - URL parameter support for pre-selected product/branch
 * - Validation before submission
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { createStockAdjustment, getProductStock } from '@/features/stock/services/stockApi';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getProducts, getProduct, ProductData } from '@/features/products/services/productsApi';
import {
    ArrowLeft,
    Save,
    Package,
    Search,
    X,
    Store,
    RefreshCw,
    Trash2,
    AlertTriangle,
    CheckCircle,
    Plus,
    Info,
    ClipboardList,
    Upload,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

type AdjustmentType = 'correction' | 'waste' | 'damage' | 'found_in_count';

interface AdjustmentItem {
    id: string;
    productId: string;
    productName: string;
    variantId: string;
    uomId: string;
    uomName: string;
    quantity: string;
    adjustmentType: AdjustmentType;
    unitCost: string;
    batchNumber: string;
    expiryDate: string;
    notes: string;
    currentStock: string;
}

interface AdjustmentTypeConfig {
    value: AdjustmentType;
    label: string;
    icon: React.ElementType;
    color: string;
    desc: string;
}

// ============================================================
// CONSTANTS
// ============================================================

const ADJUSTMENT_TYPES: AdjustmentTypeConfig[] = [
    {
        value: 'correction',
        label: 'Correction',
        icon: RefreshCw,
        color: 'border-blue-200 bg-blue-50 text-blue-700',
        desc: 'Fix incorrect stock counts',
    },
    {
        value: 'waste',
        label: 'Waste / Spoilage',
        icon: Trash2,
        color: 'border-amber-200 bg-amber-50 text-amber-700',
        desc: 'Perishable goods gone bad',
    },
    {
        value: 'damage',
        label: 'Damaged Goods',
        icon: AlertTriangle,
        color: 'border-rose-200 bg-rose-50 text-rose-700',
        desc: 'Physically damaged items',
    },
    {
        value: 'found_in_count',
        label: 'Found in Count',
        icon: CheckCircle,
        color: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        desc: 'Discovered during count',
    },
];

// ============================================================
// MAIN COMPONENT
// ============================================================

export function AdjustStockPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const user = useAuthStore((s) => s.user);
    const selectedBranchId = useAuthStore((s) => s.selectedBranchId);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    // Pre-selected values from URL
    const preselectedProductId = searchParams.get('productId') || '';
    const preselectedBranchId = searchParams.get('branchId') || '';

    // State
    const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>('correction');
    const [branchId, setBranchId] = useState<string>(preselectedBranchId || '');
    const [globalNotes, setGlobalNotes] = useState('');
    const [items, setItems] = useState<AdjustmentItem[]>([
        {
            id: `adj-${Date.now()}`,
            productId: preselectedProductId,
            productName: '',
            variantId: '',
            uomId: '',
            uomName: '',
            quantity: '',
            adjustmentType: 'correction',
            unitCost: '',
            batchNumber: '',
            expiryDate: '',
            notes: '',
            currentStock: '0',
        },
    ]);
    const [productSearch, setProductSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [showProductDropdown, setShowProductDropdown] = useState<number | null>(null);

    // Fast 120ms debounce for server search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(productSearch);
        }, 120);
        return () => clearTimeout(timer);
    }, [productSearch]);

    // Queries
    const { data: branches, isLoading: branchesLoading } = useQuery({
        queryKey: ['branches-adjust', user?.tenantId],
        queryFn: async () => {
            try {
                const data = await getBranches(user!.tenantId);
                return Array.isArray(data) ? data : [];
            } catch {
                return [];
            }
        },
        enabled: !!user?.tenantId,
    });

    // Base cache: preload top products for instantaneous 0ms client-side search while typing
    const { data: baseProducts } = useQuery<ProductData[]>({
        queryKey: ['products-adjust-base', user?.tenantId],
        queryFn: async (): Promise<ProductData[]> => {
            try {
                const response = await getProducts(user!.tenantId, {
                    limit: 300,
                    sortBy: 'name',
                    order: 'asc',
                });
                return response?.data || [];
            } catch {
                return [];
            }
        },
        enabled: !!user?.tenantId,
        staleTime: 5 * 60 * 1000,
    });

    // Debounced server search for deeper catalog items
    const { data: serverSearchResults } = useQuery<ProductData[]>({
        queryKey: ['products-adjust-search', user?.tenantId, debouncedSearch],
        queryFn: async (): Promise<ProductData[]> => {
            if (!debouncedSearch) return [];
            try {
                const response = await getProducts(user!.tenantId, {
                    limit: 50,
                    search: debouncedSearch,
                    sortBy: 'name',
                    order: 'asc',
                });
                return response?.data || [];
            } catch {
                return [];
            }
        },
        enabled: !!user?.tenantId && debouncedSearch.length >= 1,
        staleTime: 60 * 1000,
    });

    // Derived data
    const branchesList = (branches || []) as Array<{
        id: string;
        name: string;
        isMainBranch?: boolean;
    }>;

    // Ultra high speed product search: instant in-memory matching supplemented by debounced server results
    const productsList: ProductData[] = useMemo(() => {
        const q = productSearch.trim().toLowerCase();
        const base = baseProducts || [];
        const serverList = serverSearchResults || [];

        const mergedMap = new Map<string, ProductData>();
        base.forEach((p) => mergedMap.set(p.id, p));
        serverList.forEach((p) => mergedMap.set(p.id, p));
        const all = Array.from(mergedMap.values());

        if (!q) {
            return all.slice(0, 50);
        }

        // Substring matching across name, SKU, and brand for instant typing feedback
        return all
            .filter((p) => {
                const name = (p.name || '').toLowerCase();
                const sku = (p.sku || '').toLowerCase();
                const barcode = (p.barcode || '').toLowerCase();
                const brand = (p.brand || '').toLowerCase();
                return name.includes(q) || sku.includes(q) || barcode.includes(q) || brand.includes(q);
            })
            .slice(0, 50);
    }, [productSearch, baseProducts, serverSearchResults]);

    const isSingleStore = !branchesLoading && branchesList.length <= 1;

    // Auto-select branch
    useEffect(() => {
        if (branchesList.length > 0 && !branchId) {
            const preferred =
                preselectedBranchId ||
                (selectedBranchId &&
                    branchesList.find((b: any) => b.id === selectedBranchId)
                    ? selectedBranchId
                    : branchesList.find((b: any) => b.isMainBranch)?.id ||
                    branchesList[0]?.id ||
                    '');
            setBranchId(preferred);
        }
    }, [branchesList, selectedBranchId, preselectedBranchId, branchId]);

    // Auto-populate preselected product from URL parameter
    useEffect(() => {
        if (!preselectedProductId || !user?.tenantId) return;
        let isMounted = true;
        getProduct(user.tenantId, preselectedProductId).then(async (prod) => {
            if (!isMounted || !prod) return;
            const baseUom = prod.uoms?.find((u: any) => u.isBaseUom) || prod.uoms?.[0];
            let currentStock = '0';
            try {
                const stock = await getProductStock(user.tenantId, prod.id, branchId || undefined);
                if (stock) {
                    currentStock = stock.availableQuantity || stock.quantityOnHand || '0';
                }
            } catch {
                // ignore
            }
            if (!isMounted) return;
            setItems((prev) => {
                if (prev.length === 0) return prev;
                return [
                    {
                        ...prev[0],
                        productId: prod.id,
                        productName: prod.name || '',
                        uomId: baseUom?.id || '',
                        uomName: baseUom?.abbreviation || baseUom?.name || 'unit',
                        unitCost: prod.costPrice || prev[0].unitCost,
                        currentStock,
                    },
                    ...prev.slice(1),
                ];
            });
        }).catch(() => {});
        return () => { isMounted = false; };
    }, [preselectedProductId, user?.tenantId, branchId]);

    // Mutations
    const adjustMutation = useMutation({
        mutationFn: async () => {
            const validItems = items.filter(
                (i) =>
                    i.productId &&
                    i.quantity &&
                    parseFloat(i.quantity) !== 0 &&
                    i.uomId
            );
            if (validItems.length === 0)
                throw new Error('Add at least one valid product.');

            for (const item of validItems) {
                await createStockAdjustment(user!.tenantId, {
                    productId: item.productId,
                    variantId: item.variantId || undefined,
                    branchId: branchId,
                    quantity: item.quantity,
                    uomId: item.uomId,
                    adjustmentType: item.adjustmentType,
                    unitCost: item.unitCost || undefined,
                    batchNumber: item.batchNumber || undefined,
                    expiryDate: item.expiryDate || undefined,
                    notes:
                        item.notes ||
                        globalNotes ||
                        `${item.adjustmentType} adjustment for ${item.productName}`,
                });
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['stock-levels'],
            });
            queryClient.invalidateQueries({
                queryKey: ['low-stock-dash'],
            });
            queryClient.invalidateQueries({
                queryKey: ['confirm-status-dash'],
            });
            addToast({
                type: 'success',
                title: 'Stock adjusted successfully',
            });
            navigate('/inventory/levels');
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Adjustment failed',
                message: err?.message,
            });
        },
    });

    // Handlers
    const addItem = useCallback(() => {
        setItems([
            ...items,
            {
                id: `adj-${Date.now()}-${Math.random()
                    .toString(36)
                    .slice(2, 6)}`,
                productId: '',
                productName: '',
                variantId: '',
                uomId: '',
                uomName: '',
                quantity: '',
                adjustmentType,
                unitCost: '',
                batchNumber: '',
                expiryDate: '',
                notes: '',
                currentStock: '0',
            },
        ]);
    }, [items, adjustmentType]);

    const removeItem = useCallback(
        (id: string) => {
            if (items.length <= 1) {
                addToast({
                    type: 'warning',
                    title: 'Cannot remove',
                    message: 'Need at least one item.',
                });
                return;
            }
            setItems(items.filter((i) => i.id !== id));
        },
        [items, addToast]
    );

    const updateItem = useCallback(
        (id: string, field: keyof AdjustmentItem, value: string) => {
            setItems(
                items.map((i) =>
                    i.id === id ? { ...i, [field]: value } : i
                )
            );
        },
        [items]
    );

    const selectProduct = useCallback(
        async (idx: number, product: ProductData) => {
            const baseUom =
                product.uoms?.find((u: any) => u.isBaseUom) ||
                product.uoms?.[0];

            let currentStock = '0';
            try {
                const stock = await getProductStock(
                    user!.tenantId,
                    product.id,
                    branchId || undefined
                );
                if (stock)
                    currentStock =
                        stock.availableQuantity ||
                        stock.quantityOnHand ||
                        '0';
            } catch {
                // Stock fetch failed, use default
            }

            setItems(
                items.map((i, index) =>
                    index === idx
                        ? {
                            ...i,
                            productId: product.id,
                            productName: product.name || '',
                            uomId: baseUom?.id || '',
                            uomName:
                                baseUom?.abbreviation ||
                                baseUom?.name ||
                                'unit',
                            unitCost:
                                product.costPrice ||
                                i.unitCost,
                            currentStock,
                        }
                        : i
                )
            );
            setShowProductDropdown(null);
        },
        [items, user, branchId]
    );

    const validItems = items.filter(
        (i) =>
            i.productId &&
            i.quantity &&
            parseFloat(i.quantity) !== 0 &&
            i.uomId
    );
    const canSubmit = validItems.length > 0;

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() =>
                                    navigate('/inventory/levels')
                                }
                                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg shadow-emerald-500/25">
                                <RefreshCw className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-slate-900">
                                    Adjust Stock
                                </h1>
                                <p className="text-sm text-slate-500">
                                    {isSingleStore
                                        ? 'Warehouse'
                                        : branchesList.find(
                                            (b) =>
                                                b.id ===
                                                branchId
                                        )?.name ||
                                        'Select branch'}
                                    {' · '}
                                    {validItems.length} product
                                    {validItems.length !== 1
                                        ? 's'
                                        : ''}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() =>
                                    navigate(
                                        '/inventory/adjust/bulk'
                                    )
                                }
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button"
                            >
                                <Upload className="w-4 h-4" />{' '}
                                Bulk Adjust
                            </button>
                            <button
                                onClick={() =>
                                    adjustMutation.mutate()
                                }
                                disabled={
                                    !canSubmit ||
                                    adjustMutation.isPending
                                }
                                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all font-semibold shadow-lg shadow-emerald-500/25 text-sm disabled:opacity-50"
                                type="button"
                            >
                                {adjustMutation.isPending ? (
                                    <Spinner
                                        size="xs"
                                        variant="white"
                                    />
                                ) : (
                                    <Save className="w-4 h-4" />
                                )}
                                Submit Adjustment
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1200px] mx-auto p-6 lg:p-8 space-y-5">
                {/* Adjustment Type & Branch */}
                <Card>
                    <CardContent className="p-6 space-y-4">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100">
                                <ClipboardList className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-slate-800">
                                    Adjustment Details
                                </h3>
                                <p className="text-xs text-slate-500">
                                    Select type
                                    {!isSingleStore &&
                                        ' and branch'}
                                </p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                    Type
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    {ADJUSTMENT_TYPES.map(
                                        (type) => (
                                            <button
                                                key={
                                                    type.value
                                                }
                                                type="button"
                                                onClick={() => {
                                                    setAdjustmentType(
                                                        type.value
                                                    );
                                                    setItems(
                                                        items.map(
                                                            (
                                                                i
                                                            ) => ({
                                                                ...i,
                                                                adjustmentType:
                                                                    type.value,
                                                            })
                                                        )
                                                    );
                                                }}
                                                className={clsx(
                                                    'flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all text-sm',
                                                    adjustmentType ===
                                                        type.value
                                                        ? `${type.color} border-current shadow-sm`
                                                        : 'border-slate-200 text-slate-500 hover:border-slate-300'
                                                )}
                                            >
                                                <type.icon
                                                    size={
                                                        18
                                                    }
                                                    className="flex-shrink-0"
                                                />
                                                <div>
                                                    <p className="font-semibold">
                                                        {
                                                            type.label
                                                        }
                                                    </p>
                                                    <p className="text-[10px] opacity-70">
                                                        {
                                                            type.desc
                                                        }
                                                    </p>
                                                </div>
                                            </button>
                                        )
                                    )}
                                </div>
                            </div>
                            {!isSingleStore && (
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                        <Store className="w-3.5 h-3.5 inline mr-1" />{' '}
                                        Branch
                                    </label>
                                    <select
                                        value={branchId}
                                        onChange={(e) =>
                                            setBranchId(
                                                e.target
                                                    .value
                                            )
                                        }
                                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
                                    >
                                        {branchesList.map(
                                            (b) => (
                                                <option
                                                    key={
                                                        b.id
                                                    }
                                                    value={
                                                        b.id
                                                    }
                                                >
                                                    {
                                                        b.name
                                                    }
                                                </option>
                                            )
                                        )}
                                    </select>
                                    <p className="mt-2 text-xs text-slate-400 flex items-center gap-1">
                                        <Info className="w-3 h-3" />
                                        Use negative numbers
                                        (e.g., -5) to deduct
                                        stock. Positive numbers
                                        to add.
                                    </p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Products */}
                <Card>
                    <CardContent className="p-6 space-y-3">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100">
                                    <Package className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-slate-800">
                                        Products to Adjust
                                    </h3>
                                    <p className="text-xs text-slate-500">
                                        {validItems.length} valid
                                        item(s)
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={addItem}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors"
                                type="button"
                            >
                                <Plus className="w-3.5 h-3.5" />{' '}
                                Add Product
                            </button>
                        </div>

                        {items.map((item, idx) => {
                            const productUoms =
                                productsList.find(
                                    (p) =>
                                        p.id ===
                                        item.productId
                                )?.uoms || [];

                            return (
                                <motion.div
                                    key={item.id}
                                    layout
                                    initial={{
                                        opacity: 0,
                                        y: 10,
                                    }}
                                    animate={{
                                        opacity: 1,
                                        y: 0,
                                    }}
                                    className={clsx(
                                        'p-4 rounded-2xl border-2 transition-all',
                                        item.productId
                                            ? 'border-slate-200 bg-white'
                                            : 'border-dashed border-slate-300 bg-slate-50/50'
                                    )}
                                >
                                    <div className="grid grid-cols-12 gap-3 items-end">
                                        {/* Product Search */}
                                        <div className="col-span-5 relative">
                                            <label className="text-[10px] text-slate-400 mb-1 block">
                                                Product *
                                            </label>
                                            {item.productId ? (
                                                <div className="flex items-center gap-2 p-2.5 border-2 border-emerald-200 bg-emerald-50/30 rounded-xl">
                                                    <Package className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold truncate">
                                                            {
                                                                item.productName
                                                            }
                                                        </p>
                                                        <p className="text-[10px] text-slate-400">
                                                            Stock:{' '}
                                                            {parseFloat(
                                                                item.currentStock ||
                                                                '0'
                                                            ).toLocaleString()}
                                                            {item.quantity && !isNaN(parseFloat(item.quantity)) && (
                                                                <span className={clsx(
                                                                    'ml-2 font-semibold',
                                                                    parseFloat(item.currentStock || '0') + parseFloat(item.quantity) >= 0
                                                                        ? 'text-emerald-600'
                                                                        : 'text-rose-600'
                                                                )}>
                                                                    → New: {(parseFloat(item.currentStock || '0') + parseFloat(item.quantity)).toLocaleString()}
                                                                </span>
                                                            )}
                                                        </p>
                                                    </div>
                                                    <button
                                                        onClick={() =>
                                                            updateItem(
                                                                item.id,
                                                                'productId',
                                                                ''
                                                            )
                                                        }
                                                        className="p-1 text-slate-400 hover:text-rose-500"
                                                        type="button"
                                                    >
                                                        <X className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setShowProductDropdown(
                                                            showProductDropdown ===
                                                                idx
                                                                ? null
                                                                : idx
                                                        );
                                                        setProductSearch(
                                                            ''
                                                        );
                                                    }}
                                                    className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white text-left text-slate-400 hover:border-emerald-400"
                                                >
                                                    <Search className="w-3.5 h-3.5 inline mr-2" />
                                                    Search
                                                    product...
                                                </button>
                                            )}
                                            <AnimatePresence>
                                                {showProductDropdown ===
                                                    idx &&
                                                    !item.productId && (
                                                        <motion.div
                                                            initial={{
                                                                opacity: 0,
                                                                y: -5,
                                                            }}
                                                            animate={{
                                                                opacity: 1,
                                                                y: 0,
                                                            }}
                                                            exit={{
                                                                opacity: 0,
                                                                y: -5,
                                                            }}
                                                            className="absolute top-full mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl z-30 max-h-64 overflow-hidden"
                                                        >
                                                            <div className="p-2 border-b">
                                                                <div className="relative">
                                                                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Search..."
                                                                        value={
                                                                            productSearch
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            setProductSearch(
                                                                                e
                                                                                    .target
                                                                                    .value
                                                                            )
                                                                        }
                                                                        className="w-full pl-8 pr-3 py-1.5 border rounded-lg text-xs"
                                                                        autoFocus
                                                                        onClick={(
                                                                            e
                                                                        ) =>
                                                                            e.stopPropagation()
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="max-h-48 overflow-y-auto">
                                                                {productsList.length ===
                                                                    0 ? (
                                                                    <p className="p-4 text-sm text-slate-400 text-center">
                                                                        No
                                                                        products
                                                                        found
                                                                    </p>
                                                                ) : (
                                                                    productsList.map(
                                                                        (
                                                                            p: ProductData
                                                                        ) => (
                                                                            <button
                                                                                key={
                                                                                    p.id
                                                                                }
                                                                                type="button"
                                                                                onClick={() =>
                                                                                    selectProduct(
                                                                                        idx,
                                                                                        p
                                                                                    )
                                                                                }
                                                                                className="w-full text-left px-4 py-2.5 hover:bg-slate-50 text-sm border-b last:border-0"
                                                                            >
                                                                                <p className="font-medium truncate">
                                                                                    {
                                                                                        p.name
                                                                                    }
                                                                                </p>
                                                                                <p className="text-xs text-slate-400">
                                                                                    {p.sku ||
                                                                                        p.brand ||
                                                                                        ''}
                                                                                </p>
                                                                            </button>
                                                                        )
                                                                    )
                                                                )}
                                                            </div>
                                                        </motion.div>
                                                    )}
                                            </AnimatePresence>
                                        </div>

                                        {/* Quantity */}
                                        <div className="col-span-2">
                                            <label className="text-[10px] text-slate-400 mb-1 block">
                                                Quantity *
                                            </label>
                                            <input
                                                type="number"
                                                step="0.001"
                                                value={
                                                    item.quantity
                                                }
                                                onChange={(e) =>
                                                    updateItem(
                                                        item.id,
                                                        'quantity',
                                                        e.target
                                                            .value
                                                    )
                                                }
                                                placeholder={
                                                    adjustmentType ===
                                                        'correction'
                                                        ? '± qty'
                                                        : '-5'
                                                }
                                                className="w-full px-2 py-2.5 border-2 border-slate-200 rounded-lg text-sm text-right focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
                                            />
                                        </div>

                                        {/* UOM */}
                                        <div className="col-span-2">
                                            <label className="text-[10px] text-slate-400 mb-1 block">
                                                UOM *
                                            </label>
                                            {productUoms.length >
                                                0 ? (
                                                <select
                                                    value={
                                                        item.uomId
                                                    }
                                                    onChange={(
                                                        e
                                                    ) => {
                                                        const selectedUom =
                                                            productUoms.find(
                                                                (
                                                                    uom: any
                                                                ) =>
                                                                    uom.id ===
                                                                    e
                                                                        .target
                                                                        .value
                                                            );
                                                        updateItem(
                                                            item.id,
                                                            'uomId',
                                                            e
                                                                .target
                                                                .value
                                                        );
                                                        if (
                                                            selectedUom
                                                        )
                                                            updateItem(
                                                                item.id,
                                                                'uomName',
                                                                selectedUom.abbreviation ||
                                                                selectedUom.name ||
                                                                ''
                                                            );
                                                    }}
                                                    className="w-full px-2 py-2.5 border-2 border-slate-200 rounded-lg text-xs bg-white"
                                                >
                                                    {productUoms.map(
                                                        (
                                                            uomItem: any
                                                        ) => (
                                                            <option
                                                                key={
                                                                    uomItem.id
                                                                }
                                                                value={
                                                                    uomItem.id
                                                                }
                                                            >
                                                                {uomItem.abbreviation ||
                                                                    uomItem.name}
                                                            </option>
                                                        )
                                                    )}
                                                </select>
                                            ) : (
                                                <input
                                                    value={
                                                        item.uomName ||
                                                        '—'
                                                    }
                                                    disabled
                                                    className="w-full px-2 py-2.5 border-2 border-slate-200 rounded-lg text-xs bg-slate-50"
                                                />
                                            )}
                                        </div>

                                        {/* Unit Cost */}
                                        <div className="col-span-2">
                                            <label className="text-[10px] text-slate-400 mb-1 block">
                                                Unit Cost (₦)
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={
                                                    item.unitCost
                                                }
                                                onChange={(e) =>
                                                    updateItem(
                                                        item.id,
                                                        'unitCost',
                                                        e.target
                                                            .value
                                                    )
                                                }
                                                placeholder="Cost"
                                                className="w-full px-2 py-2.5 border-2 border-slate-200 rounded-lg text-sm text-right focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
                                            />
                                        </div>

                                        {/* Remove */}
                                        <div className="col-span-1 flex items-end pb-2">
                                            <button
                                                onClick={() =>
                                                    removeItem(
                                                        item.id
                                                    )
                                                }
                                                className="p-2 text-slate-400 hover:text-rose-500 rounded-lg w-full flex justify-center"
                                                type="button"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Extended fields */}
                                    {item.productId && (
                                        <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-3">
                                            <input
                                                type="text"
                                                value={
                                                    item.batchNumber
                                                }
                                                onChange={(e) =>
                                                    updateItem(
                                                        item.id,
                                                        'batchNumber',
                                                        e.target
                                                            .value
                                                    )
                                                }
                                                placeholder="Batch Number (optional)"
                                                className="px-3 py-2 border-2 border-slate-200 rounded-lg text-xs"
                                            />
                                            <input
                                                type="date"
                                                value={
                                                    item.expiryDate
                                                }
                                                onChange={(e) =>
                                                    updateItem(
                                                        item.id,
                                                        'expiryDate',
                                                        e.target
                                                            .value
                                                    )
                                                }
                                                className="px-3 py-2 border-2 border-slate-200 rounded-lg text-xs"
                                            />
                                            <input
                                                type="text"
                                                value={
                                                    item.notes
                                                }
                                                onChange={(e) =>
                                                    updateItem(
                                                        item.id,
                                                        'notes',
                                                        e.target
                                                            .value
                                                    )
                                                }
                                                placeholder="Item notes (optional)"
                                                className="px-3 py-2 border-2 border-slate-200 rounded-lg text-xs"
                                            />
                                        </div>
                                    )}
                                </motion.div>
                            );
                        })}

                        {/* Global Notes */}
                        <div className="pt-3">
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                General Notes
                            </label>
                            <textarea
                                value={globalNotes}
                                onChange={(e) =>
                                    setGlobalNotes(
                                        e.target.value
                                    )
                                }
                                rows={2}
                                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white resize-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
                                placeholder="Overall reason for this adjustment..."
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Bottom Actions */}
                <div className="flex justify-end gap-3">
                    <button
                        onClick={() =>
                            navigate('/inventory/levels')
                        }
                        className="px-5 py-3 border-2 border-slate-300 rounded-xl hover:bg-white transition font-medium text-sm"
                        type="button"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() =>
                            adjustMutation.mutate()
                        }
                        disabled={
                            !canSubmit ||
                            adjustMutation.isPending
                        }
                        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all font-semibold text-sm shadow-lg shadow-emerald-500/25 disabled:opacity-50"
                        type="button"
                    >
                        {adjustMutation.isPending ? (
                            <Spinner
                                size="xs"
                                variant="white"
                            />
                        ) : (
                            <Save className="w-4 h-4" />
                        )}
                        Submit Adjustment
                    </button>
                </div>
            </div>
        </div>
    );
}